import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import { COMMON_BREEDS, ANIMAL_CATEGORIES } from '../../../constants/workflowGranular';
import { MARK } from '../../../constants';
import { racaPadrao } from '../../../utils/animais';

/**
 * Modal de cadastro RÁPIDO de animais em campo
 * Apenas 2 campos obrigatórios: identificação + raça
 */
const QuickAnimalRegister = ({
  show, onClose, onSave, loading,
  initialCategory = null, initialIdentifier = '',
  clients = [], initialClientId = null,
}) => {
  const [formData, setFormData] = useState({
    identifier: initialIdentifier || '',
    breed: racaPadrao(initialCategory),
    category: initialCategory,
    rgd: '',
    client_id: initialClientId || '',
  });
  const [customBreed, setCustomBreed] = useState('');
  const [errors, setErrors] = useState({});

  // Reseta o formulário toda vez que o modal abre
  useEffect(() => {
    if (show) {
      setFormData({
        identifier: initialIdentifier || '',
        breed: racaPadrao(initialCategory),
        category: initialCategory,
        rgd: '',
        // Já vem preenchido com o cliente do serviço: o animal aspirado no
        // serviço da Fazenda X quase sempre é dela. Continua editável, e
        // limpável para o animal que é do laboratório.
        client_id: initialClientId || '',
      });
      setCustomBreed('');
      setErrors({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.identifier?.trim()) {
      newErrors.identifier = 'Identificação é obrigatória';
    }

    const effectiveBreed = formData.breed === 'Outra' ? customBreed.trim() : formData.breed?.trim();
    if (!effectiveBreed) {
      newErrors.breed = formData.breed === 'Outra' ? 'Digite o nome da raça' : 'Raça é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    // Auto-definir sexo baseado na categoria
    let sex = null;
    if (formData.category === 'Reprodutor' || formData.category === 'Touro') {
      sex = 'M';
    } else if (formData.category === 'Doadora' || formData.category === 'Receptora') {
      sex = 'F';
    }

    const effectiveBreed = formData.breed === 'Outra' ? customBreed.trim() : formData.breed.trim();

    onSave({
      identifier: formData.identifier.trim(),
      breed: effectiveBreed,
      category: formData.category,
      rgd: formData.rgd?.trim() || null,
      sex: sex,
      client_id: formData.client_id || null,
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <JanelaModal
      chave="cadastro-rapido-animal"
      titulo="Cadastro Rápido de Animal"
      subtitulo="Apenas identificação e raça são obrigatórios"
      onFechar={onClose}
      fecharDesabilitado={loading}
      largura="sm:max-w-md"
      camada="z-[60]"
    >
      <div className="p-4 sm:p-6">

        {/* Form */}
        <div className="space-y-4">
          {/* Identificação (OBRIGATÓRIO) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Identificação <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Brinco, nome ou número"
              className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 ${
                errors.identifier
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-slate-300 focus:ring-blue-500'
              }`}
              value={formData.identifier}
              onChange={(e) => handleChange('identifier', e.target.value)}
              onKeyPress={handleKeyPress}
              autoFocus
              disabled={loading}
            />
            {errors.identifier && (
              <p className="mt-1 text-sm text-red-600">{MARK.WARN} {errors.identifier}</p>
            )}
          </div>

          {/* Raça (OBRIGATÓRIO) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Raça <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 ${
                errors.breed
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-slate-300 focus:ring-blue-500'
              }`}
              value={formData.breed}
              onChange={(e) => {
                handleChange('breed', e.target.value);
                if (e.target.value !== 'Outra') setCustomBreed('');
              }}
              disabled={loading}
            >
              <option value="">Selecione...</option>
              {COMMON_BREEDS.map(breed => (
                <option key={breed} value={breed}>{breed}</option>
              ))}
            </select>
            {formData.breed === 'Outra' && (
              <input
                type="text"
                placeholder="Digite o nome da raça..."
                autoFocus
                className={`mt-2 w-full px-4 py-3 text-base border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.breed
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-slate-300 focus:ring-blue-500'
                }`}
                value={customBreed}
                onChange={(e) => {
                  setCustomBreed(e.target.value);
                  if (errors.breed) setErrors(prev => ({ ...prev, breed: null }));
                }}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            )}
            {errors.breed && (
              <p className="mt-1 text-sm text-red-600">{MARK.WARN} {errors.breed}</p>
            )}
          </div>

          {/* Categoria (OPCIONAL) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Categoria <span className="text-slate-400 text-xs">(opcional)</span>
            </label>
            <select
              className="w-full px-4 py-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.category || ''}
              onChange={(e) => {
                const categoria = e.target.value || null;
                handleChange('category', categoria);
                if (!formData.breed || formData.breed === racaPadrao(formData.category)) {
                  handleChange('breed', racaPadrao(categoria));
                }
              }}
              disabled={loading}
            >
              <option value="">Não especificar</option>
              {Object.values(ANIMAL_CATEGORIES).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* RGD (OPCIONAL) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              RGD <span className="text-slate-400 text-xs">(opcional)</span>
            </label>
            <input
              type="text"
              placeholder="Registro genealógico"
              className="w-full px-4 py-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.rgd}
              onChange={(e) => handleChange('rgd', e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
          </div>

          {/* CLIENTE (OPCIONAL) */}
          {clients.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Cliente / Fazenda <span className="text-slate-400 text-xs">(opcional)</span>
              </label>
              <select
                className="w-full px-4 py-3 text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.client_id || ''}
                onChange={(e) => handleChange('client_id', e.target.value)}
                disabled={loading}
              >
                <option value="">Sem vínculo</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.contact ? `${c.name} — ${c.contact}` : c.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                De quem é o animal. Deixe sem vínculo se for do próprio laboratório.
              </p>
            </div>
          )}
        </div>

        {/* Footer - Dica */}
        <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
          <p className="text-xs text-blue-800">
            💡 <strong>Dica:</strong> Você pode completar os dados do animal depois.
            O cadastro rápido serve apenas para identificar o animal no campo.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base transition-colors min-h-[48px]"
          >
            <Check className="w-5 h-5" />
            {loading ? 'Cadastrando...' : 'Cadastrar'}
          </button>
          <button
            onClick={onClose}
            disabled={loading}
            className="sm:px-6 py-3 bg-slate-500 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50 transition-colors font-medium text-base min-h-[48px]"
          >
            Cancelar
          </button>
        </div>
      </div>
    </JanelaModal>
  );
};

export default QuickAnimalRegister;
